import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FunctionalDialog from 'components/Modals/FunctionalDialog';
import { DownloadOption } from 'types';
import { useSubscription } from 'services/hooks';
import { SongType } from 'services/api/entities';
import { RadioButton } from 'components/Modals/implementations/DownloadDialog/components/RadioButton/RadioButton';
import { getIsLockedValues } from 'components/Modals/implementations/DownloadDialog/helpers';

interface PropsType {
  songType: SongType;
  isLoading: boolean;
  isOpen: boolean;
  onSubmit: (downloadOption: DownloadOption) => void;
  onClose: () => void;
}

/**
 * Dialog that displays all the available download options (mp3, wav, pdf, etc.)
 */

const DownloadSelectionDialog = ({
  songType,
  isLoading,
  isOpen,
  onSubmit,
  onClose,
}: PropsType) => {
  const { t } = useTranslation();

  const { isFreeAccount } = useSubscription();

  const canRenderStems = [
    SongType.AI_GENERATED,
    SongType.CUSTOMIZABLE,
    SongType.STUDIO,
  ].includes(songType);

  const canRenderSamplePack = [
    SongType.AI_GENERATED,
    SongType.CUSTOMIZABLE,
    SongType.STUDIO,
  ].includes(songType);

  const [fileType, setFileType] = useState<DownloadOption>(DownloadOption.Mp3);

  const makeOnClickHandler = (exportOption: DownloadOption) => () =>
    setFileType(exportOption);

  const {
    isMp3Locked,
    isWavLocked,
    isPdfLocked,
    isStemsLocked,
    isSamplePackLocked,
  } = getIsLockedValues(isFreeAccount);

  const getIsRadioSelected = (radioFileType: DownloadOption) =>
    radioFileType === fileType;

  const handleClose = () => {
    setFileType(DownloadOption.Mp3);
    onClose();
  };

  return (
    <FunctionalDialog
      closeText={t('general:action:cancel')}
      state={{ isOpen, isLoading, error: null }}
      submitText={t('download:action:download')}
      title={t('download:selection:title')}
      action={{
        onSubmit: () => onSubmit(fileType),
        onClose: handleClose,
        onExited: handleClose,
      }}
    >
      <RadioButton
        downloadOption={DownloadOption.Mp3}
        onClick={makeOnClickHandler(DownloadOption.Mp3)}
        isSelected={getIsRadioSelected(DownloadOption.Mp3)}
        isLocked={isMp3Locked}
      />

      <RadioButton
        downloadOption={DownloadOption.Wav}
        onClick={makeOnClickHandler(DownloadOption.Wav)}
        isSelected={getIsRadioSelected(DownloadOption.Wav)}
        isLocked={isWavLocked}
      />

      {canRenderStems && (
        <RadioButton
          downloadOption={DownloadOption.Stems}
          onClick={makeOnClickHandler(DownloadOption.Stems)}
          isSelected={getIsRadioSelected(DownloadOption.Stems)}
          isLocked={isStemsLocked}
        />
      )}
      {canRenderSamplePack && (
        <RadioButton
          downloadOption={DownloadOption.SamplePack}
          onClick={makeOnClickHandler(DownloadOption.SamplePack)}
          isSelected={getIsRadioSelected(DownloadOption.SamplePack)}
          isLocked={isSamplePackLocked}
          labelText={t('songList:newSong')}
        />
      )}
      <RadioButton
        downloadOption={DownloadOption.PdfLicense}
        onClick={makeOnClickHandler(DownloadOption.PdfLicense)}
        isSelected={getIsRadioSelected(DownloadOption.PdfLicense)}
        isLocked={isPdfLocked}
      />
    </FunctionalDialog>
  );
};

export default DownloadSelectionDialog;
